import React, { useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import { Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { PlaylistEmptyIllustration } from '../assets/PlaylistEmptyIllustration';
import { PlaylistEmptyNoQueueIllustration } from '../assets/PlaylistEmptyNoQueueIllustration';

const messages = defineMessages({
	emptyPlaylistHeading: {
		id: 'ai-audio.playlist.empty.heading',
		defaultMessage: 'Queue up some work',
		description: 'Heading shown when the playlist is empty',
	},
	emptyPlaylistDescription: {
		id: 'ai-audio.playlist.empty.description',
		defaultMessage:
			'Tap {audioIcon} on Pages and Live Docs, then click {addToPlaylistIcon} to add to your list',
		description: 'Description shown when the playlist is empty, explaining how to add items',
	},
	audioIconLabel: {
		id: 'ai-audio.playlist.empty.audio-icon-label',
		defaultMessage: 'Audio briefing icon',
		description:
			'Accessibility label for the audio briefing icon in the empty playlist description',
	},
	addToPlaylistIconLabel: {
		id: 'ai-audio.playlist.empty.addToPlaylist-icon-label',
		defaultMessage: 'Add to playlist icon',
		description:
			'Accessibility label for the add to playlist icon in the empty playlist description',
	},
	emptyPlaylistHeadingV2: {
		id: 'ai-audio.playlist.empty.v2.heading',
		defaultMessage: 'Nothing here yet',
		description: 'Heading shown in the updated empty playlist state',
	},
	emptyPlaylistDescriptionV2: {
		id: 'ai-audio.playlist.empty.v2.description',
		defaultMessage: 'Tap the add-to-playlist button when you see it to add item to playlist.',
		description:
			'Description shown in the updated empty playlist state, explaining how to add items',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		flexGrow: 1,
	},
	content: {
		width: '290px',
		textAlign: 'center',
		paddingLeft: token('space.400'),
		paddingRight: token('space.400'),
	},
	containerV2: {
		flex: 1,
		flexGrow: 1,
		marginInline: 'auto',
		maxWidth: '320px',
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		textAlign: 'center',
	},
});

export const PlaylistEmptyView = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { sendTrackEvent } = usePlatformInstrumentation();

	useEffect(() => {
		sendTrackEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'playlistEmpty',
			actionSubjectId: 'playlist',
			source: 'playlist',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (
		FeatureGates.getExperimentValue(
			'confluence_audio_updated_playlist_empty_state',
			'isEnabled',
			false,
		)
	) {
		return (
			<Stack xcss={styles.containerV2} alignInline="center" space="space.150">
				<Heading size="small" as="h3">
					{formatMessage(messages.emptyPlaylistHeadingV2)}
				</Heading>
				<PlaylistEmptyNoQueueIllustration />
				<Flex alignItems="center" direction="row" columnGap="space.200">
					<PlaylistAddIcon label="" />
					<Text size="medium" align="start">
						{formatMessage(messages.emptyPlaylistDescriptionV2)}
					</Text>
				</Flex>
			</Stack>
		);
	}

	return (
		<Flex
			xcss={styles.container}
			justifyContent="center"
			alignItems="center"
			rowGap="space.200"
			direction="column"
		>
			<PlaylistEmptyIllustration />
			<Flex xcss={styles.content} alignItems="center" rowGap="space.150" direction="column">
				<Heading size="small" as="h3">
					{formatMessage(messages.emptyPlaylistHeading)}
				</Heading>
				<Text size="medium" color="color.text.subtlest">
					{formatMessage(messages.emptyPlaylistDescription, {
						audioIcon: (
							<AiGenerativeAudioBriefingIcon label={formatMessage(messages.audioIconLabel)} />
						),
						addToPlaylistIcon: (
							<PlaylistAddIcon label={formatMessage(messages.addToPlaylistIconLabel)} />
						),
					})}
				</Text>
			</Flex>
		</Flex>
	);
};
